import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { Trash2, ShoppingBag, Plus, Minus } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { BACKEND_URL } from '../lib/api';

const CartPage = () => {
  const navigate = useNavigate();
  const { cart, removeFromCart, updateQuantity, getTotal, getItemCount } = useCart();
  const [feesEnabled, setFeesEnabled] = useState(true);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/config`)
      .then((r) => setFeesEnabled(r.data?.booking_fees_enabled !== false))
      .catch(() => setFeesEnabled(true));
  }, []);

  /**
   * Try to bump a cart line by +1, but ping the backend for live availability
   * first so the user gets a "Only X remain" toast instead of a failed
   * checkout later. The backend `POST /orders` is still the authoritative
   * atomic guard — this is just a friendlier client-side pre-check.
   */
  const tryIncreaseQuantity = async (item, index) => {
    try {
      const res = await axios.get(
        `${BACKEND_URL}/api/tickets/${item.ticketType.ticket_id}/availability`,
      );
      const remaining = Number(res.data?.remaining ?? 0);
      if (remaining <= item.quantity) {
        if (remaining <= 0) {
          toast.error(`${item.ticketType.type_name} is sold out.`);
        } else {
          const word = remaining === 1 ? 'ticket remains' : 'tickets remain';
          toast.error(`Only ${remaining} ${word} for ${item.ticketType.type_name}.`);
        }
        // Clamp to whatever's actually left.
        updateQuantity(item.event.event_id, item.ticketType.ticket_id, Math.max(0, remaining));
        return;
      }
    } catch {
      // Network hiccup — allow the change and let the checkout endpoint do
      // the authoritative check.
    }
    updateQuantity(item.event.event_id, item.ticketType.ticket_id, item.quantity + 1);
  };

  /* ===== Booking Fee Logic (mirrors backend; respects runtime toggle).
     Free tickets (R0) NEVER incur a curation fee. ===== */
  const calculateBookingFee = (price) => {
    if (price <= 0) return 0;
    if (!feesEnabled) return 0;
    if (price < 150) return 10;
    if (price < 300) return 12;
    if (price < 600) return 15;
    return 20;
  };

  /* What the CUSTOMER actually pays in fees — depends on the event's
     booking_fee_strategy. VinePass always receives the full fee; absorbed
     portions come out of the organiser's payout. */
  const customerPaidFee = (price, event) => {
    const full = calculateBookingFee(price);
    if (full <= 0) return 0;
    const strategy = (event && event.booking_fee_strategy) || 'customer_pays';
    if (strategy === 'organiser_absorbs') return 0;
    if (strategy === 'split_50_50') return Math.round((full / 2) * 100) / 100;
    return full;
  };

  const subtotal = getTotal();
  const totalFees = cart.reduce(
    (sum, item) => sum + customerPaidFee(item.ticketType.price, item.event) * item.quantity, 0
  );
  const grandTotal = subtotal + totalFees;

  if (cart.length === 0) {
    return (
      <div className="min-h-screen bg-[#FAFAF7] flex items-center justify-center">
        <div className="text-center" data-testid="empty-cart-message">
          <ShoppingBag className="w-16 h-16 text-[#4A5D50] mx-auto mb-4" />
          <h2 className="text-2xl font-semibold text-[#112318] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
            Your cart is empty
          </h2>
          <p className="text-[#4A5D50] mb-6">Start adding some amazing wine farm experiences!</p>
          <Button
            onClick={() => navigate('/events')}
            className="bg-[#1F3D2B] text-white hover:bg-[#172E20] rounded-full px-8 py-3"
            data-testid="browse-events-link"
          >
            Browse Events
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF7]">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1
          className="text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none font-bold text-[#112318] mb-8"
          style={{ fontFamily: 'Playfair Display, serif' }}
          data-testid="cart-title"
        >
          Your Cart
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4" data-testid="cart-items-list">
            {cart.map((item, index) => (
              <div
                key={`${item.event.event_id}-${item.ticketType.ticket_id}`}
                className="bg-white rounded-xl p-6 shadow-sm border border-[#E5E9E6]"
                data-testid={`cart-item-${index}`}
              >
                <div className="flex gap-3 sm:gap-4">
                  <img
                    src={item.event.image_url}
                    alt={item.event.title}
                    className="w-20 h-20 sm:w-24 sm:h-24 object-cover rounded-lg shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <h3
                          className="text-base sm:text-lg font-semibold text-[#112318] mb-1"
                          style={{ fontFamily: 'Playfair Display, serif' }}
                          data-testid="cart-item-event-title"
                        >
                          {item.event.title}
                        </h3>
                        <p className="text-sm text-[#4A5D50] mb-2" data-testid="cart-item-ticket-type">
                          {item.ticketType.type_name}
                        </p>
                        <p className="text-sm text-[#4A5D50]" data-testid="cart-item-price">
                          R{item.ticketType.price.toFixed(2)} each
                        </p>
                      </div>
                      <p className="text-lg sm:text-xl font-bold text-[#1F3D2B] shrink-0" data-testid={`cart-item-subtotal-${index}`}>
                        R{(item.ticketType.price * item.quantity).toFixed(2)}
                      </p>
                    </div>

                    <div className="flex flex-wrap items-center gap-3 mt-3">
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => updateQuantity(item.event.event_id, item.ticketType.ticket_id, item.quantity - 1)}
                          className="h-8 w-8"
                          data-testid={`decrease-cart-quantity-${index}`}
                        >
                          <Minus className="w-3 h-3" />
                        </Button>
                        <span className="text-sm font-medium w-8 text-center" data-testid={`cart-item-quantity-${index}`}>
                          {item.quantity}
                        </span>
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => tryIncreaseQuantity(item, index)}
                          className="h-8 w-8"
                          data-testid={`increase-cart-quantity-${index}`}
                        >
                          <Plus className="w-3 h-3" />
                        </Button>
                      </div>

                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeFromCart(item.event.event_id, item.ticketType.ticket_id)}
                        className="text-[#B04D4D] hover:text-[#B04D4D] hover:bg-[#B04D4D]/10"
                        data-testid={`remove-cart-item-${index}`}
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        Remove
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl p-6 shadow-sm border border-[#E5E9E6] sticky top-24" data-testid="order-summary">
              <h3
                className="text-xl font-semibold text-[#112318] mb-4"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                Order Summary
              </h3>

              <div className="space-y-2 mb-4">
                <div className="flex justify-between text-[#4A5D50]">
                  <span>Subtotal ({getItemCount()} tickets)</span>
                  <span>R{subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-[#4A5D50]">
                  <span>Booking Experience Fee</span>
                  <span>R{totalFees.toFixed(2)}</span>
                </div>
              </div>

              <div className="border-t border-[#E5E9E6] pt-4 mb-6">
                <div className="flex justify-between text-lg font-bold text-[#112318]">
                  <span>Total</span>
                  <span data-testid="cart-total">R{grandTotal.toFixed(2)}</span>
                </div>
              </div>

              <Button
                onClick={() => navigate('/checkout')}
                className="w-full bg-[#1F3D2B] text-white hover:bg-[#172E20] rounded-full py-6 font-medium transition-all shadow-md text-lg"
                data-testid="proceed-to-checkout-button"
              >
                Proceed to Checkout
              </Button>

              <Button
                onClick={() => navigate('/events')}
                variant="outline"
                className="w-full mt-3 rounded-full"
                data-testid="continue-shopping-button"
              >
                Continue Shopping
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartPage;